import * as Sentry from '@sentry/browser';
import isEmpty from 'lodash/isEmpty';
import { useReducer, createContext, useEffect, useCallback, useMemo, useRef, useState } from 'react';

import { IntegrationSystem } from '@/shared/types';
import { ConditionView } from '@/shared/ui';

import { Loader } from '@/shared/ui';

import { useGAUserSystems } from '@/hooks/useGAUserSystems';

import { config } from '@/config/index';

import TokenManager from '@/middlewares/auth/TokenManager';

import { getStoredConfig } from '@/api/apiService';
import {
  ResponseStoredConfig,
  UserData,
  DocumentData,
  ResponseUserData,
  SocialProtectData,
  AddressType,
  LivingAddress,
  RegisteredAddress,
  BirthAddress,
} from '@/api/types';

import api from '@/api';

import { useQuery } from '@tanstack/react-query';

import { persistedQueryKeys, queryKeys } from '@/api/queryKeys';

import { queryClient } from '../../lib/queryClient';

import { refreshTokens, requestLogout, singInWithPassword } from '../../api/auth';
import {
  allAllowedToChoosePassportDocumentTypeCodes,
  allowedForSelectionDocumentTypesForDeclaredPlaceOfResidence,
  allowedToChooseAddressRegDocumentTypeCodes,
} from '../../components/Forms/constants';
import { die, emit, EventLogin, on } from '../../eventBus';
import { useGAUserId } from '../../hooks/useGAUserId';
import {
  clearWasLoggedIn,
  normalizeSessionExpiredReloadState,
  setWasLoggedIn,
  wasLoggedIn,
} from '../SessionExpired/utils';

import { AUTH, authReducer } from './authReducer';
import { removeTokens, TokensI, saveTokens, restoreTokens } from './authTokens';

export interface UserPersonalInfo {
  userData: Nullable<UserData>;
  allDocuments: DocumentData[];
  documentsData: Record<AddressType.LIVING | AddressType.REGISTRATION | 'passport', Nullable<DocumentData>>;
  addressesData: {
    [AddressType.BIRTH]: BirthAddress;
    [AddressType.LIVING]: LivingAddress;
    [AddressType.REGISTRATION]: RegisteredAddress;
  };
  address_equal: boolean;
  fullName: string;
  systems: IntegrationSystem[];
  socialProtect?: Nullable<SocialProtectData>;
  extra?: boolean;
}

export interface AuthContextInterface extends UserPersonalInfo {
  isLoggedIn: boolean;
  storedLinks: ResponseStoredConfig;
  isInitRender: boolean;
  isLoading: boolean;
  loginWithPassword: (password: string, email: string) => Promise<Nullable<void>>;
  logout: () => void;
  updateUserData: (data: ResponseUserData | null) => void;
  handleSessionCheckSum: (authChecksum: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextInterface>({
  fullName: '',
  userData: {} as UserData,
  allDocuments: [],
  documentsData: {} as Record<Partial<AddressType> | 'passport', DocumentData>,
  isLoggedIn: false,
  loginWithPassword: async () => null,
  logout: () => null,
  updateUserData: () => null,
  systems: [],
  isInitRender: false,
  storedLinks: { videoInstruction: { deafSupport: '' } } as ResponseStoredConfig,
  socialProtect: {} as SocialProtectData,
  addressesData: {} as {
    [AddressType.BIRTH]: BirthAddress;
    [AddressType.LIVING]: LivingAddress;
    [AddressType.REGISTRATION]: RegisteredAddress;
  },
  address_equal: false,
  isLoading: false,
  handleSessionCheckSum: () => Promise.resolve(),
});
const tokenManager = TokenManager.getInstance();
const accessToken = tokenManager.getAccessToken();

export const AuthContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [{ isLoggedIn }, dispatch] = useReducer(authReducer, {
    isLoggedIn: !!accessToken,
    isAuthentificated: false,
  });
  const {
    data: storedLinks,
    isLoading: isLoadingLinks,
    isFetched: haveLinksBeenFetched,
  } = useQuery({
    queryKey: queryKeys.auth.storedConfig(),
    queryFn: getStoredConfig,
  });
  const {
    data: profile,
    isLoading: isLoadingProfile,
    isFetched: hasProfileBeenFetched,
  } = useQuery({
    queryKey: queryKeys.auth.profile(),
    queryFn: api.getUserData,
    enabled: isLoggedIn,
  });

  const loadProfile = (): void => {
    queryClient.invalidateQueries({ queryKey: queryKeys.auth.profile() });
  };

  const updateUserData = (data: ResponseUserData | null): void => {
    queryClient.setQueryData(queryKeys.auth.profile(), data);
  };

  const [isInitRender, setIsInitRender] = useState<boolean>(true);
  const didInitSessionCheckRef = useRef(false);

  const {
    user: userData = null,
    documents: documentsData = [],
    msp: socialProtect = null,
    addresses: addressesData = null,
    address_equal = false,
    systems = [],
  } = useMemo<ResponseUserData['data']>(() => {
    if (!isLoggedIn || !profile?.data) return {} as ResponseUserData['data'];

    return profile.data;
  }, [isLoggedIn, profile?.data]);

  const addresses = useMemo(
    () =>
      ({
        [AddressType.BIRTH]:
          addressesData?.find((address) => address.type === AddressType.BIRTH) || ({} as BirthAddress),
        [AddressType.LIVING]:
          addressesData?.find((address) => address.type === AddressType.LIVING) || ({} as LivingAddress),
        [AddressType.REGISTRATION]:
          addressesData?.find((address) => address.type === AddressType.REGISTRATION) || ({} as RegisteredAddress),
      }) as {
        [AddressType.BIRTH]: BirthAddress;
        [AddressType.LIVING]: LivingAddress;
        [AddressType.REGISTRATION]: RegisteredAddress;
      },
    [addressesData],
  );

  const login = useCallback((tokens: TokensI) => {
    saveTokens(tokens);
    setWasLoggedIn();
    dispatch({ type: AUTH.LOGIN });
  }, []);

  const { clearUserId } = useGAUserId(profile?.data?.uid);

  const { clearUserSystems } = useGAUserSystems(profile?.data?.systems);

  const logout = useCallback(async () => {
    Sentry.setUser(null);
    updateUserData(null);
    try {
      await requestLogout();
    } catch (e) {
      console.error(e);
    }
    removeTokens();
    clearWasLoggedIn();
    queryClient.removeQueries({
      predicate: (query) => {
        const [first, second] = query.queryKey as string[];
        return !persistedQueryKeys.some(([k0, k1]) => k0 === first && (k1 === undefined || k1 === second));
      },
    });
    dispatch({ type: AUTH.LOGOUT });
    clearUserId();
    clearUserSystems();
  }, [updateUserData, clearUserId]);

  const loginWithPassword = useCallback(async (password: string, email: string) => {
    emit({
      action: 'login',
      tokens: await singInWithPassword(email, password),
    });
  }, []);

  const handleSessionCheckSum = useCallback(
    async (authChecksum: Nullable<string>) => {
      if (isLoggedIn) return;

      try {
        const lastRefreshTime = localStorage.getItem('lastTokensRefresh');
        if (
          !lastRefreshTime ||
          !config.auth.refreshTimeout ||
          Date.now() - Number(lastRefreshTime) < config.auth.refreshTimeout
        ) {
          emit({
            action: 'login',
            tokens: await refreshTokens({
              authChecksum,
              refreshToken: authChecksum ? null : (await restoreTokens()).refresh_token,
              withLogout: false,
            }),
          });
        }
      } catch (error) {
        if (wasLoggedIn()) {
          normalizeSessionExpiredReloadState();
          clearWasLoggedIn();
        }
      } finally {
        setIsInitRender(false);
      }
    },
    [isLoggedIn],
  );

  useEffect(() => {
    normalizeSessionExpiredReloadState();

    on('login', ({ tokens }: EventLogin) => {
      login(tokens);
    });
    on('logout', logout);
    on('update_profile', loadProfile);

    return (): void => {
      die('login');
      die('logout');
      die('update_profile');
    };
  }, []);

  useEffect(() => {
    if (!isEmpty(profile)) {
      Sentry.setUser({
        id: profile.data.uid,
        name: `${profile.data.user.name_first} ${profile.data.user.name_last}`,
        email: profile.data.user.email,
      });
    }
  }, [profile]);

  useEffect(() => {
    if (didInitSessionCheckRef.current) {
      if (isLoggedIn) {
        loadProfile();
      }
      return;
    }

    didInitSessionCheckRef.current = true;

    if (isLoggedIn) {
      loadProfile();
      return;
    }

    if (normalizeSessionExpiredReloadState()) {
      setIsInitRender(false);
      return;
    }

    handleSessionCheckSum(null);
  }, [isLoggedIn]);

  const showLoader =
    isInitRender || (isLoadingProfile && !hasProfileBeenFetched) || (isLoadingLinks && !haveLinksBeenFetched);

  if (isEmpty(storedLinks)) {
    return <Loader centered />;
  }

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        logout,
        loginWithPassword,
        fullName: [userData?.name_first, userData?.name_last].join(' '),
        userData,
        updateUserData,
        isInitRender,
        isLoading: showLoader,
        addressesData: addresses,
        allDocuments: documentsData ?? [],
        documentsData: {
          [AddressType.LIVING]: documentsData?.find(
            (document) =>
              document.type && allowedForSelectionDocumentTypesForDeclaredPlaceOfResidence.includes(document.type),
          ),
          [AddressType.REGISTRATION]: documentsData?.find(
            (document) => document.type && allowedToChooseAddressRegDocumentTypeCodes.includes(document.type),
          ),
          passport: documentsData?.find(
            (document) => document.type && allAllowedToChoosePassportDocumentTypeCodes.includes(document.type),
          ),
        },
        systems,
        storedLinks,
        socialProtect,
        address_equal,
        handleSessionCheckSum,
      }}
    >
      <ConditionView if={showLoader}>
        <Loader centered />
      </ConditionView>

      <div
        id="auth-provider-container"
        style={{
          display: showLoader ? 'none' : 'block',
        }}
      >
        {children}
      </div>
    </AuthContext.Provider>
  );
};

export default AuthContext;
